import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  OnChanges,
  OnInit,
  output,
  SimpleChanges
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  Validator,
  Validators,
  FormControl
} from '@angular/forms';

import find from 'lodash-es/find';
import some from 'lodash-es/some';

import { IFBCustomFormField, IFBCustomFormFieldAttribute, IFBCustomFormFieldResponse } from '@app/models/custom-form';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { skip } from 'rxjs/operators';

@Component({
  selector: 'app-custom-form-field',
  templateUrl: './custom-form-field.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomFormFieldComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CustomFormFieldComponent),
      multi: true
    }
  ],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomFormFieldComponent implements OnInit, OnChanges, ControlValueAccessor, Validator {
  config = input<IFBCustomFormField>();
  locale = input<string>();
  showErrors = input<boolean>();
  updated = output<IFBCustomFormFieldResponse>();

  field = new FormControl();
  onTouched: () => void;
  onChange: (value: any) => void;

  constructor() {
    this.field.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => this.propagateUpdate());
    this.field.valueChanges.pipe(takeUntilDestroyed(), skip(1)).subscribe(e => this.onChange(e));
  }

  ngOnInit(): void {
    if (this.config().mandatory) {
      this.field.addValidators(Validators.requiredTrue);
    }

    this.field.updateValueAndValidity({ onlySelf: false, emitEvent: true });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.showErrors && changes.showErrors.currentValue) {
      this.field.markAsDirty();
    }
  }

  writeValue(value: string): void {
    if (value) {
      this.field.setValue(value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  validate(control: AbstractControl) {
    return this.field.errors;
  }

  getAttribute(attrType: string): IFBCustomFormFieldAttribute | undefined {
    return find(this.config().attributes, { attributeType: attrType });
  }

  hasAttribute(attrType: string): boolean {
    return some(this.config().attributes, { attributeType: attrType });
  }

  /**
   * Searches for a translation of an attribute for the locale binding.
   * If translation not found, falls back to the first available translation.
   * In case no translations available returns empty string.
   */
  getLocalization(attrType: string): string {
    const attr = this.getAttribute(attrType);

    if (!attr || !attr.localizations || !attr.localizations.length) {
      // No translations available
      return '';
    }

    const localization = find(attr.localizations, { locale: this.locale() });

    if (localization) {
      return localization.text;
    }

    // Fallback to first localization available
    return attr.localizations[0].text;
  }

  propagateUpdate(): void {
    this.updated.emit({
      id: this.config().id,
      response: this.field.value
    });
  }
}
